import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Info } from "lucide-react";
import { Button, Panel, PanelHeader, RuleList } from "../../components/primitives";
import {
  ARTIFACT_COUNT,
  EXPORT_RULES,
  GATE_COUNT,
  GATE_SPECS,
  PHASE_SPECS,
  VALIDATOR_COUNT,
  artifactsInGroup,
  gateIdFromHash
} from "../../lib/pipeline";
import { Disclosure } from "./Disclosure";
import { ReferenceTable } from "./ReferenceTable";

/**
 * /pipeline — THE EXPORT GATE SPECIFICATION.
 *
 * THE SCREEN'S HONEST JOB. It answers three questions and nothing else: what must be true
 * before ATLAS will export, what exactly each check checks, and what you get when it passes.
 * It is REFERENCE. It shows NO run's state.
 *
 * Therefore it renders ZERO StatusPills, ZERO GateMeter chrome, ZERO ticks and ZERO rings.
 * That is the trust split the whole product rests on: static spec goes in RuleList and
 * ReferenceTable; LIVE state goes in DataList and GateMeter. This screen is 100% the static
 * side of it, and the harness asserts the absence — [data-testid="status-pill"] must have
 * length 0 here, and the page text must contain neither "Approved" nor "Needs review".
 *
 * WHAT WAS HERE BEFORE. Nine fabricated verdicts: the compiler's BUILD STATE was mapped onto a
 * RUN verdict pill, so "Phase 1 · ATLAS package onboarding" rendered a green APPROVED pill
 * whose own tooltip read "A human recorded an approval for this run." That is gone and cannot
 * come back: there is no run in scope here to render a verdict from.
 *
 * WHAT THIS ROUND FIXES, AND THE ONE IDEA BEHIND ALL OF IT.
 *
 * This page is the product's SPEC SHEET — the page an operator, or their compliance officer,
 * opens to answer "what does this thing guarantee, what exactly does it check, and what do I
 * get." It is a reference document, not a dashboard. The previous pass got that right in
 * substance and then rendered it as three white rectangles on a grey desk: the six gate
 * SENTENCES — the actual reference content — were hidden behind chevrons, so the hero column
 * was a 600px void in its resting state while a twelve-row rail ran long beside it; the 21
 * validator ids and 12 filenames were undifferentiated plain mono; and final.html — the
 * document the entire product exists to produce — had no more presence than fonts/.
 *
 * Four structural consequences, and each one is a fix for one of those:
 *
 *   1. THE MASTHEAD carries the guarantee in serif, and a stat strip whose three numbers are
 *      DERIVED (GATE_COUNT / VALIDATOR_COUNT / ARTIFACT_COUNT). No literal 6, 21 or 12 is
 *      typed anywhere in this file. If the backend adds a validator, this page says 22 without
 *      anyone editing it — which is the whole reason the counts are computed from the spec.
 *
 *   2. THE GATE SENTENCE IS ALWAYS VISIBLE, IN FULL — no clamp, no ellipsis, closed or open, at
 *      320 and at 1920. The page is useful without a single click. The two-line clamp the last
 *      round used bought column balance at the cost of trailing four of the six checks off into
 *      "…", and a specification does not trail off. The column it balanced is gone (see below).
 *
 *   3. THE RUN PACKAGE LEADS WITH THE DOCUMENT. final.html and final.pdf render inside a real
 *      document card — serif title, hairline rule, a paper stack — captioned "Illustration of
 *      the deliverable. This page shows no run." It is a picture of the deliverable, not a claim
 *      about one. ARTIFACT_SPECS remains the single source, the count stays derived, and the
 *      groups are presentational only.
 *
 *   4. THE COMPILER BUILD STATUS IS AN APPENDIX. Same copy, same nine phases, same seven honest
 *      state words — closed, last, on a lower-contrast ground, its summary a filled row rather
 *      than a slab.
 *
 *   5. THE PAGE IS ONE COLUMN AT EVERY WIDTH. Its primary content is an accordion: "Expand all"
 *      takes the gate list from 1259px to 2257px at 1440. A static panel beside it is stranded
 *      the instant the operator does what the page invites — which is exactly what happened.
 *      The width goes INSIDE the panels instead (spec plate, side-by-side non-negotiables, 3-up
 *      validator grid, landscape document sheet, one row per file). A dead column is not
 *      possible here, in any state, rather than merely absent in the resting one.
 *
 * THE THUMB-REACHABLE PRIMARY ACTION IS **NOT APPLICABLE** HERE, and I am declaring that rather
 * than manufacturing one. This screen has no mutation. The one-handed path is already provided
 * by the chrome: the fixed bottom tab bar carries Review as a permanent 44px destination in the
 * thumb zone. The only control this page owns is Expand/Collapse all — a real capability over
 * real state, not a fake one.
 */
export function PipelinePage() {
  const { hash } = useLocation();

  // The page owns the open set, not each Disclosure. The URL hash can open a gate at any
  // time — including while this page is already mounted, e.g. a blocked gate on /review links
  // to /pipeline#protected-facts — and a defaultOpen held inside the child would be
  // initialised once and then ignore every later hash change.
  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    const deepLinked = gateIdFromHash(hash);
    return new Set(deepLinked ? [deepLinked] : []);
  });
  const [buildOpen, setBuildOpen] = useState(false);

  const deepLinked = gateIdFromHash(hash);
  const allOpen = openIds.size === GATE_SPECS.length;

  useEffect(() => {
    if (!deepLinked) return;
    setOpenIds((current) => (current.has(deepLinked) ? current : new Set(current).add(deepLinked)));

    const target = document.getElementById(deepLinked);
    if (!target) return;
    // SCROLL ONLY. NEVER .focus().
    // The chrome's route-change effect focuses the <h1>. A competing .focus() here would
    // reproduce the exact F4 bug — the Sheet stealing focus back from the element that had
    // legitimately claimed it — on a new surface, and a keyboard operator arriving here from a
    // blocked gate would be yanked away from the page they asked for. scroll-margin-top on the
    // row (see pipeline.css) clears the sticky header. behavior:"auto" is instant, which is
    // also the correct behaviour under prefers-reduced-motion.
    target.scrollIntoView({ block: "start", behavior: "auto" });
  }, [deepLinked]);

  function toggleGate(id: string, open: boolean) {
    setOpenIds((current) => {
      const next = new Set(current);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const documentArtifacts = artifactsInGroup("document");
  const evidenceArtifacts = artifactsInGroup("evidence");
  const provenanceArtifacts = artifactsInGroup("provenance");

  return (
    <div className="pipeline-page" data-testid="pipeline-page">
      {/* ---------------------------------------------------------------- THE MASTHEAD.
          The product's STRONGEST TRUE GUARANTEE was once fenced inside an amber alert box —
          amber is reserved by contract for CAUTION and PROTECTED, so painting the best true
          thing the app can say as a warning was a semantic error — and then, correcting that,
          it was flattened into a grey paragraph. It is neither now: it is the one sentence the
          whole product rests on, set in the document face, on a card with ground and depth. */}
      <section className="pipeline-masthead">
        <p className="pipeline-guarantee">
          ATLAS will not export a package until all {GATE_COUNT} checks pass.
        </p>
        <p className="pipeline-subline">This page is what each one checks.</p>
        <p className="pipeline-honesty">
          <Info aria-hidden="true" />
          <span>
            Specification only. Nothing on this page is a result — your run's checks are on
            Review.
          </span>
        </p>

        {/* DERIVED, NEVER TYPED. A spec sheet that cannot count its own checks has no business
            telling anyone their document is safe. */}
        {/* dt (the label) precedes dd (the value) in the DOM — that is the order a screen reader
            must hear ("Checks that must pass: 6") and the order the HTML spec requires. The
            value is lifted above it VISUALLY by `order` in the stylesheet, which changes nothing
            for assistive technology. */}
        <dl className="pipeline-stats">
          <div className="pipeline-stat">
            <dt className="pipeline-stat-label">Checks that must pass</dt>
            <dd className="pipeline-stat-value">{GATE_COUNT}</dd>
          </div>
          <div className="pipeline-stat">
            <dt className="pipeline-stat-label">Automated validators</dt>
            <dd className="pipeline-stat-value">{VALIDATOR_COUNT}</dd>
          </div>
          <div className="pipeline-stat">
            <dt className="pipeline-stat-label">Items in the package</dt>
            <dd className="pipeline-stat-value">{ARTIFACT_COUNT}</dd>
          </div>
        </dl>
      </section>

      <div className="pipeline-layout">
        {/* ---------------------------------------------------------------- THE HERO.
            Above the fold at 390px. The spec comes first; the compiler's build status is last. */}
        <Panel className="pipeline-gate-panel" data-testid="gate-spec">
          {/* No icon. The contract permits one only when a panel maps 1:1 to a nav destination.
              `meta` is legal here because it carries a count the title does not — and it is 21,
              not "V1-V19", which undercounts by two (V8b and V14a are extra ids, not sub-cases). */}
          <PanelHeader
            title="Export gate"
            meta={`${GATE_COUNT} checks · ${VALIDATOR_COUNT} validators`}
          />

          {/* THE TWO NON-NEGOTIABLES, on their own amber ground with a FULL four-sided hairline.
              Amber is reserved by contract for CAUTION and PROTECTED, and these two rules are
              precisely the protected, non-waivable part of the contract — so this is the one
              place on the screen where colour carries meaning rather than decoration. Full
              border, never an edge rail: the banned pattern does not appear on this page.

              Exactly TWO rules, not three. "Every check passes", "no warnings" and "no un-run
              check counts as a pass" are ONE fact stated three different ways. */}
          <div className="pipeline-rules">
            <RuleList items={EXPORT_RULES} label="Export gate rules" />
          </div>

          {/* NOT a PanelSection. The primitive applies --panel-pad to itself, and every
              disclosure row inside it applies --panel-pad too — so the summary titles would land
              at TWO panel-pads while the panel header title landed at one. Composed from tokens. */}
          <section className="pipeline-checks">
            <div className="pipeline-checks-head">
              <h3 className="pipeline-checks-title">The six checks</h3>
              {/* A REAL capability over REAL state — the open set this page already owns. It
                  fabricates nothing and it is not a progress affordance. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setOpenIds(allOpen ? new Set() : new Set(GATE_SPECS.map((gate) => gate.id)))
                }
              >
                {allOpen ? "Collapse all" : "Expand all"}
              </Button>
            </div>

            {GATE_SPECS.map((gate, index) => (
              <Disclosure
                key={gate.id}
                id={gate.id}
                className="pipeline-gate-row"
                // The ordinal spine. A specification is ORDERED, and these six are the same six,
                // in the same order, that the operator meets on /review.
                ordinal={String(index + 1).padStart(2, "0")}
                title={gate.label}
                // The reference content, visible with zero clicks. CSS clamps it to two lines
                // while the card is closed and releases it when the card opens.
                sentence={gate.sentence}
                meta={gate.countLabel}
                // The codes this check runs, readable without a click at >= 768. Derived from the
                // same validator list the table renders — nothing is typed twice.
                codes={gate.validators.map((validator) => validator.id)}
                // Reviewer sign-off has NO validator, so its meta is not a count and must not
                // look like one. Neutral chip — and no pass/fail colour anywhere near it.
                metaVariant={gate.validators.length ? "count" : "neutral"}
                open={openIds.has(gate.id)}
                onToggle={(open) => toggleGate(gate.id, open)}
                deepLinked={deepLinked === gate.id}
                testId="gate-spec-row"
                summaryTestId="gate-spec-summary"
              >
                {/* Reviewer sign-off runs NO validator, so it renders no table. An empty table
                    with a "0" would be decoration pretending to be data; it says so in words. */}
                {gate.validators.length ? (
                  <ReferenceTable
                    items={gate.validators}
                    idWidth="narrow"
                    variant="chip"
                    label={`${gate.label} validators`}
                    testId="validator-table"
                    rowTestId="validator-row"
                  />
                ) : (
                  <p className="pipeline-gate-note">
                    No validator runs for this check. It reads the verdicts recorded for the run,
                    and the app cannot clear it for you.
                  </p>
                )}
              </Disclosure>
            ))}
          </section>
        </Panel>

        {/* ---------------------------------------------------------------- THE PACKAGE.
            DOM ORDER IS THE READING ORDER AT EVERY WIDTH: spec, then deliverable, then
            appendix. There is no column reshuffle at any breakpoint — the page is one column
            everywhere, because its primary content is an accordion that triples in height when
            the operator hits "Expand all", and nothing static can sit beside that without being
            stranded. The width is spent inside these panels instead. */}
        <Panel className="pipeline-package-panel" data-testid="package-spec">
          <PanelHeader title="Run package" meta={`${ARTIFACT_COUNT} items`} />

          <div className="pipeline-panel-body">
            <p className="pipeline-panel-caption">
              Produced when a run is packaged. Every package contains the same items.
            </p>

            {/* THE DOCUMENT. This is a document product and the document is the deliverable, so
                it is RENDERED as a document — serif title, hairline rule, a stack of sheets
                behind it — rather than listed as two filenames among twelve. The caption keeps
                it honest: an illustration of the deliverable, never a claim about a run. */}
            <div className="package-doc">
              <article className="package-doc-card" aria-labelledby="package-doc-title">
                <div className="package-doc-head">
                  <p className="package-doc-eyebrow">The deliverable</p>
                  <h4 className="package-doc-title" id="package-doc-title">
                    ATLAS DPI Concept Note
                  </h4>
                </div>
                <div className="package-doc-body">
                  <p className="package-doc-line">
                    The canonical concept note, personalized for one audience and rendered to
                    paper.
                  </p>
                  <ReferenceTable
                    items={documentArtifacts}
                    idWidth="wide"
                    label="Document files"
                    rowTestId="artifact-row"
                  />
                </div>
              </article>
              <p className="package-doc-caption">
                Illustration of the deliverable. This page shows no run.
              </p>
            </div>

            {/* THE OTHER TEN. Two quiet sub-labels — presentational only. ARTIFACT_SPECS stays
                the single source and ARTIFACT_COUNT stays derived from it. */}
            <div className="package-list">
              <section className="package-group">
                <h4 className="package-group-title">Evidence and reports</h4>
                <ReferenceTable
                  items={evidenceArtifacts}
                  idWidth="wide"
                  label="Evidence and reports"
                  testId="artifact-table"
                  rowTestId="artifact-row"
                />
              </section>
              <section className="package-group">
                <h4 className="package-group-title">Provenance and assets</h4>
                <ReferenceTable
                  items={provenanceArtifacts}
                  idWidth="wide"
                  label="Provenance and assets"
                  rowTestId="artifact-row"
                />
              </section>
            </div>
          </div>
        </Panel>

        {/* ---------------------------------------------------------------- THE APPENDIX.
            A single closed disclosure on a lower-contrast ground, LAST on the page and last for
            a screen reader. At >= 768 its summary spans the row — title left, its one-sentence
            caveat beside it — so a 44px control on a 1072px card is a filled row rather than a
            slab with dead air in the middle of it.
            The content is PRESERVED (no capability lost): the nine verdict pills are deleted and
            the seven real state words survive as prose instead of collapsing into two.
            FLAGGED FOR THE OWNER: this describes the compiler's own build, not the operator's
            run, and arguably does not belong in an operator tool at all. Demoting rather than
            deleting, because deleting product content is his call, not mine. */}
        <Panel className="pipeline-build-panel" data-testid="build-status">
          <Disclosure
            title="Compiler build status"
            meta="About the compiler itself, not your run."
            open={buildOpen}
            onToggle={setBuildOpen}
            testId="build-status-row"
            summaryTestId="build-status-summary"
          >
            <p className="pipeline-gate-note">
              Nine build phases and where each one stands. Nothing here is a check, and nothing
              here has been verified against a document.
            </p>
            {/* COMPACT, not wide. The phase ids are "Phase 0".."Phase 8" — seven mono
                characters. They were sharing the 280px track measured for
                "audience-personalization-memo.pdf", which left ~225px of dead gutter on every
                one of the nine rows and broke the label away from its own sentence. */}
            <ReferenceTable
              items={PHASE_SPECS}
              idWidth="compact"
              label="Compiler build phases"
              testId="phase-table"
              rowTestId="phase-row"
            />
          </Disclosure>
        </Panel>
      </div>
    </div>
  );
}
